import { createStore } from '@aibees/shared/store';
import type { Draft } from 'immer';
import { NotificationItem, NotificationStatus } from '../types';
import { NotificationStoreData as StoreData } from './types';

const useNotificationStore = createStore<StoreData>(
  'notification',
  (set, get) => ({
    notifications: [],
    seenNotifications: new Set<string>(),
    // Scroll-related state
    containerRef: null,
    loadingMore: false,
    page: 0,
    hasMore: true,
    isLoadingInitial: true,
    // Drawer state
    isDrawerOpen: false,

    setNotifications: (newNotificationsOrUpdater) => {
      set((state) => ({
        notifications:
          typeof newNotificationsOrUpdater === 'function'
            ? newNotificationsOrUpdater(state.notifications)
            : newNotificationsOrUpdater,
      }));
    },
    clearNotifications: (group) =>
      set(({ notifications }) => ({
        notifications: notifications.filter((n) => n.message.group !== group),
      })),
    addNotification: (item) =>
      set(({ notifications }) =>
        notifications.unshift(item as Draft<NotificationItem>)
      ),
    markNotificationAsRead: (item) =>
      set(({ notifications }) => {
        const found = notifications.find((x) => x.id === item.id);
        if (found) found.status = NotificationStatus.DELIVERED;
      }),
    markNotificationAsSeen: (notificationId) =>
      set(({ seenNotifications }) => {
        const newSeenNotifications = new Set(seenNotifications);
        newSeenNotifications.add(notificationId);
        return { seenNotifications: newSeenNotifications };
      }),
    markAllNotificationsAsSeen: () =>
      set(({ notifications, seenNotifications }) => {
        const newSeenNotifications = new Set(seenNotifications);
        notifications.forEach((notification) => {
          newSeenNotifications.add(notification.id);
        });
        return { seenNotifications: newSeenNotifications };
      }),
    notificationTab: NotificationStatus.ALL,
    setNotificationTab: (notificationTab) => set({ notificationTab }),

    // Drawer methods
    openDrawer: () => set({ isDrawerOpen: true }),
    closeDrawer: () => set({ isDrawerOpen: false }),
    toggleDrawer: () => set((state) => ({ isDrawerOpen: !state.isDrawerOpen })),

    // Scroll-related methods
    getContainerRef: () => get().containerRef,
    setContainerRef: (ref) => set({ containerRef: ref }),
    setLoadingMore: (loading) => set({ loadingMore: loading }),
    setPage: (page) => set({ page }),
    setHasMore: (hasMore) => set({ hasMore }),
    setIsLoadingInitial: (loading) => set({ isLoadingInitial: loading }),
    handleScroll: (event) => {
      const target = event.target as HTMLElement;
      const threshold = target.scrollHeight * 0.8;
      const state = get();

      if (
        target.scrollTop + target.clientHeight >=
          target.scrollHeight - threshold &&
        !state.loadingMore &&
        state.hasMore
      ) {
        set({ loadingMore: true, page: state.page + 1 });
      }
    },
    setNotificationConfig: (notificationConfig) => set({ notificationConfig }),
    notificationConfig: null,
  })
);

export default useNotificationStore;
